type Entry<T> = { value: T; expires: number }

/**
 * Tiny TTL cache with in-flight de-duplication. Kick's api/v2 is undocumented
 * and we do not want to hammer it while the user clicks between tabs.
 */
export class TtlCache {
  private entries = new Map<string, Entry<unknown>>()
  private inflight = new Map<string, Promise<unknown>>()

  constructor(private readonly defaultTtlMs: number) {}

  get<T>(key: string): T | undefined {
    const hit = this.entries.get(key)
    if (!hit) return undefined
    if (hit.expires < Date.now()) {
      this.entries.delete(key)
      return undefined
    }
    return hit.value as T
  }

  set<T>(key: string, value: T, ttlMs = this.defaultTtlMs): void {
    this.entries.set(key, { value, expires: Date.now() + ttlMs })
  }

  /** Returns the cached value, otherwise runs `fn` once and caches the result. */
  async fetch<T>(key: string, ttlMs: number, fn: (signal: AbortSignal) => Promise<T>): Promise<T> {
    const cached = this.get<T>(key)
    if (cached !== undefined) return cached

    const pending = this.inflight.get(key) as Promise<T> | undefined
    if (pending) return pending

    const controller = new AbortController()
    const promise = fn(controller.signal)
      .then((value) => {
        this.set(key, value, ttlMs)
        return value
      })
      .finally(() => {
        this.inflight.delete(key)
        controller.abort()
      })

    this.inflight.set(key, promise)
    return promise
  }

  invalidate(prefix: string): void {
    for (const key of [...this.entries.keys()]) {
      if (key.startsWith(prefix)) this.entries.delete(key)
    }
  }
}

export const kickCache = new TtlCache(120_000)
export const chatCache = new TtlCache(30 * 60_000)